import React from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";

type Tone = "default" | "info" | "success" | "warning" | "danger";

export interface DetailSummaryTag {
  label: string;
  tone?: Tone;
  icon?: React.ReactNode;
}

export interface DetailSummaryStatus {
  label: string;
  tone?: Tone;
  icon?: LucideIcon;
  meta?: string;
}

export interface DetailSummarySection {
  title: string;
  content: React.ReactNode;
}

export interface DetailSummaryCardProps
  extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  description?: React.ReactNode;
  tags?: DetailSummaryTag[];
  status?: DetailSummaryStatus;
  sections?: DetailSummarySection[];
  footerStart?: React.ReactNode;
  footerEnd?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

const STATUS_TEXT_CLASS: Record<Tone, string> = {
  default: "text-muted-foreground",
  info: "text-sky-600 dark:text-sky-400",
  success: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  danger: "text-destructive",
};

const TAG_CLASS: Record<Tone, string> = {
  default:
    "border-border/80 bg-muted/40 text-muted-foreground dark:border-border dark:bg-primary/5",
  info: "border-sky-100 bg-sky-50 text-sky-700 dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-300",
  success:
    "border-emerald-100 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300",
  warning:
    "border-amber-100 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300",
  danger:
    "border-destructive/30 bg-destructive/10 text-destructive dark:border-destructive/40 dark:bg-destructive/20",
};

const DetailSummaryTagPill: React.FC<DetailSummaryTag> = ({
  label,
  tone = "default",
  icon,
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold tracking-wide uppercase",
      TAG_CLASS[tone]
    )}
  >
    {icon}
    {label}
  </span>
);

export const DetailSummaryCard: React.FC<DetailSummaryCardProps> = ({
  icon,
  title,
  subtitle,
  description,
  tags,
  status,
  sections,
  footerStart,
  footerEnd,
  children,
  className,
  ...rest
}) => {
  const StatusIcon = status?.icon;
  const statusTone = status?.tone ?? "default";

  return (
    <div
        className={cn(
          "rounded-2xl border border-border/70 bg-card/90 shadow-sm backdrop-blur-sm",
          className
        )}
        {...rest}
    >
      <div className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            {icon && (
              <div className="text-3xl leading-none text-primary/80">{icon}</div>
            )}
            <div>
              <h3 className="text-xl font-semibold tracking-tight">{title}</h3>
              {subtitle && (
                <p className="text-sm text-muted-foreground">{subtitle}</p>
              )}
            </div>
          </div>
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {tags.map((tag) => (
                <DetailSummaryTagPill key={tag.label} {...tag} />
              ))}
            </div>
          )}
        </div>

        {status && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            {StatusIcon && (
              <StatusIcon
                className={cn("h-4 w-4", STATUS_TEXT_CLASS[statusTone])}
              />
            )}
            <span
              className={cn(
                "font-medium capitalize",
                STATUS_TEXT_CLASS[statusTone]
              )}
            >
              {status.label}
            </span>
            {status.meta && (
              <>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">{status.meta}</span>
              </>
            )}
          </div>
        )}

        {description && (
          <div className="mt-4 text-sm text-muted-foreground">{description}</div>
        )}

        {sections && sections.length > 0 && (
          <div className="mt-6 space-y-5">
            {sections.map((section) => (
              <div key={section.title} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.title}
                </p>
                <div className="text-sm leading-relaxed text-foreground/80">
                  {section.content}
                </div>
              </div>
            ))}
          </div>
        )}

        {children}
      </div>

      {(footerStart || footerEnd) && (
        <div className="flex flex-col gap-3 border-t border-border/70 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          {footerStart && <div className="flex flex-wrap gap-2">{footerStart}</div>}
          {footerEnd}
        </div>
      )}
    </div>
  );
};

export default DetailSummaryCard;
